<script setup>
import { useLanguageStore } from "@/stores/languageStore.js";
import { computed, onActivated, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCustomStarMapStore } from "@/stores/customStarMapStore.js";
import { shareCustomMap } from "@/utils/shareCustomMap.js";
import StarMapWorkshopNav from "@/components/StarMapWorkshopNav.vue";
import { getWorkshopSafetyStatus, submitMapAppeal } from "@/api/customMapApi.js";
import { localizedCreatorText } from "@/utils/localizedCreatorText.js";

const route = useRoute();
const router = useRouter();
const languageStore = useLanguageStore();
const store = useCustomStarMapStore();
const toastText = ref("");
const toastKind = ref("info");
const toastMapId = ref("");
const loading = ref(false);
let toastTimer = 0;
const searchQuery = ref("");
const maps = computed(() => store.maps);
const safety = ref(null);
const appealMapId = ref("");
const appealText = ref("");
const displayName = map => localizedCreatorText(languageStore, map?.name, "name");
const displayDescription = map => localizedCreatorText(languageStore, map?.description, "description");

function showToast(message, kind = "info", mapId = "") {
  window.clearTimeout(toastTimer);
  toastText.value = message;
  toastKind.value = kind;
  toastMapId.value = String(mapId || "");
  toastTimer = window.setTimeout(() => {
    toastText.value = "";
    toastMapId.value = "";
  }, 1000);
}

const filteredMaps = computed(() => {
  const keyword = searchQuery.value.trim().toLocaleLowerCase();
  if (!keyword) return maps.value;
  return maps.value.filter(map => {
    const haystack = `${map.name || ""} ${map.description || ""}`.toLocaleLowerCase();
    return haystack.includes(keyword);
  });
});

function goParent() {
  router.push({ name: "workshop" });
}

function localizedError(error, fallbackKey = "saveFailed") {
  const message = String(error?.message || "");
  if (Number(error?.status) >= 500 || /internal server error/i.test(message)) {
    return languageStore.t(fallbackKey);
  }
  if (error?.code === "PUBLISH_LIMIT") return languageStore.t("publishLimitReached");
  if (error?.code === "WORKSHOP_BANNED") return languageStore.t("workshopBanned");
  return message || languageStore.t(fallbackKey);
}

function counts(map) {
  const built = map.nodes.filter(node => store.isLevelBuilt(map.id, node.level)).length;
  const lit = map.nodes.filter(node => map.litLevels?.[String(node.level)]).length;
  return { built, lit, total: map.nodes.length };
}
function visibilityChangedKey(value) {
  if (value === "public") return "visibilityChangedPublic";
  if (value === "share") return "visibilityChangedShare";
  return "visibilityChangedPrivate";
}

function moderationLabel(map) {
  if (map.visibility === "private") return "";
  return languageStore.t(`moderation_${map.moderationStatus || "pending"}`);
}


async function changeVisibility(map, event) {
  const nextVisibility = String(event?.target?.value || "private");
  const previousVisibility = map.visibility;

  try {
    if (store.backendEnabled) {
      await store.updateRemoteMap(map.id, { visibility: nextVisibility });
    } else {
      store.updateVisibility(map.id, nextVisibility);
    }
    showToast(
      languageStore.t(visibilityChangedKey(nextVisibility)),
      "success",
      map.id
    );
  } catch (error) {
    if (event?.target) event.target.value = previousVisibility;
    showToast(
      localizedError(error),
      "error",
      map.id
    );
  }
}

async function removeMap(map) {
  if (!window.confirm(languageStore.t("confirmDeleteMap", { name: map.name }))) return;
  try {
    if (store.backendEnabled) await store.deleteRemoteMap(map.id);
    else store.deleteMap(map.id);
    showToast(languageStore.t("starMapCleared"), "success");
  } catch (error) {
    showToast(error?.message || languageStore.t("starMapSaveError"), "error", map.id);
  }
}

async function shareMap(map) {
  if (map.visibility === "private") {
    showToast(languageStore.t("shareUnavailable"), "error", map.id);
    return;
  }

  try {
    let shareTarget = map;
    if (store.backendEnabled) {
      const link = await store.createRemoteShareLink(map.id);
      shareTarget = {
        ...store.getMap(map.id),
        shareCode: link?.shareCode || store.getMap(map.id)?.shareCode,
        shareUrl: store.getMap(map.id)?.shareUrl
      };
    }

    const result = await shareCustomMap(shareTarget);
    if (result.mode === "cancelled") return;
    showToast(languageStore.t("shareReady"), "success", map.id);
  } catch (error) {
    showToast(error?.message || languageStore.t("shareFailed"), "error", map.id);
  }
}

async function sendAppeal(map) {
  if (appealText.value.trim().length < 5) return showToast(languageStore.t("appealTooShort"), "error", map.id);
  try {
    await submitMapAppeal(map.id, appealText.value);
    appealMapId.value = "";
    appealText.value = "";
    showToast(languageStore.t("appealSubmitted"), "success", map.id);
  } catch (error) {
    showToast(error?.message || languageStore.t("saveFailed"), "error", map.id);
  }
}

async function loadMaps() {
  if (!store.backendEnabled) {
    store.refresh();
    return;
  }

  loading.value = true;
  try {
    await store.loadRemoteMaps();
    await store.loadRemoteChallengeMap();
    safety.value = await getWorkshopSafetyStatus();
    await Promise.allSettled(
      store.maps.map(map => store.loadRemoteProgress(map.id))
    );
  } catch (error) {
    showToast(error?.message || languageStore.t("unavailable"), "error");
  } finally {
    loading.value = false;
  }
}

onMounted(loadMaps);
onActivated(loadMaps);

function handleModerationSync(event) {
  if (event?.type === "storage" && event.key !== "workshop-moderation-revision") return;
  loadMaps();
}

onMounted(() => {
  window.addEventListener("workshop-moderation-changed", handleModerationSync);
  window.addEventListener("storage", handleModerationSync);
});

onBeforeUnmount(() => {
  window.clearTimeout(toastTimer);
  window.removeEventListener("workshop-moderation-changed", handleModerationSync);
  window.removeEventListener("storage", handleModerationSync);
});
</script>

<template>
  <section class="manager page-card">
    <div class="page-header subpage-header">
      <button class="back-button" type="button" @click="goParent">← {{ languageStore.t("back") }}</button>
    </div>

    <StarMapWorkshopNav />
    <p v-if="safety" class="safety-summary" :class="{ banned:safety.banned }">{{ safety.banned ? languageStore.t('workshopBanned') : languageStore.t('dailyPublishRemaining',{count:Math.max(0,safety.dailyPublishLimit-safety.publishesToday)}) }}</p>

    <div class="subview-title">
      <strong></strong>
      <span></span>
    </div>

    <div v-if="maps.length" class="map-search" role="search">
      <span class="search-icon" aria-hidden="true">⌕</span>
      <input
        v-model="searchQuery"
        type="search"
        inputmode="search"
        autocomplete="off"
        :placeholder="languageStore.t('searchMaps')"
        :aria-label="languageStore.t('searchMaps')"
      />
      <button v-if="searchQuery" class="search-clear" type="button" :aria-label="languageStore.t('clearSearch')" @click="searchQuery = ''">×</button>
    </div>

    <div v-if="maps.length && filteredMaps.length" class="map-list">
      <article v-for="map in filteredMaps" :key="map.id" class="map-card">
        <Transition name="card-toast-pop">
          <div
            v-if="toastText && toastMapId === String(map.id)"
            class="map-card-toast"
            :class="`is-${toastKind}`"
            role="status"
            aria-live="polite"
          >
            {{ toastText }}
          </div>
        </Transition>
        <button class="map-preview" type="button" @click="router.push({ name: 'custom-map-view', params: { mapId: map.id }, query: { from: 'map-manager' } })">
          <img :src="map.imageDataUrl" alt="" />
        </button>
        <div class="map-copy">
          <strong>{{ displayName(map) }}</strong>
          <p class="map-card-description">{{ displayDescription(map) }}</p>
          <span>{{ languageStore.t("builtLevels", { done: counts(map).built, total: counts(map).total }) }} · {{ languageStore.t("litLevels", { done: counts(map).lit, total: counts(map).total }) }}</span>
          <span v-if="moderationLabel(map)" class="moderation-status" :class="`is-${map.moderationStatus}`">
            {{ moderationLabel(map) }}<template v-if="map.moderationReason">：{{ map.moderationReason }}</template>
          </span>
          <button v-if="['rejected','hidden'].includes(map.moderationStatus)" class="appeal-open" type="button" @click="appealMapId=appealMapId===map.id?'':map.id">{{ languageStore.t('submitAppeal') }}</button>
          <div v-if="appealMapId===map.id" class="appeal-box"><textarea v-model="appealText" maxlength="500" :placeholder="languageStore.t('appealPlaceholder')"/><button type="button" @click="sendAppeal(map)">{{ languageStore.t('submitAppeal') }}</button></div>
          <label>
            <small>{{ languageStore.t("mapPermission") }}</small>
            <select :value="map.visibility" @change="changeVisibility(map, $event)">
              <option value="private">{{ languageStore.t("privateMap") }}</option>
              <option value="share">{{ languageStore.t("shareMap") }}</option>
              <option value="public">{{ languageStore.t("publicMap") }}</option>
            </select>
          </label>
        </div>
        <div class="map-actions">
          <button type="button" @click="router.push({ name: 'custom-map-view', params: { mapId: map.id }, query: { from: 'map-manager' } })">{{ languageStore.t("viewCompletedMap") }}</button>
          <button type="button" @click="router.push({ name: 'star-map-editor', query: { mapId: map.id, from: 'map-manager' } })">{{ languageStore.t("starMapEdit") }}</button>
          <button type="button" @click="shareMap(map)">{{ languageStore.t("shareAction") }}</button>
          <button class="danger" type="button" @click="removeMap(map)">{{ languageStore.t("deleteAction") }}</button>
        </div>
      </article>
    </div>
    <div v-else-if="maps.length" class="search-empty">
      <strong>{{ languageStore.t("noMatches") }}</strong>
      <span>{{ languageStore.t("customMapDescription") }}</span>
      <button type="button" @click="searchQuery = ''">{{ languageStore.t("myCustomMaps") }}</button>
    </div>
    <div v-else class="empty-state">
      <strong>{{ languageStore.t("noCustomMaps") }}</strong>
      <span></span>
      <button class="neon-button" type="button" @click="router.push({ name: 'star-map-editor', query: { from: 'map-manager' } })">{{ languageStore.t("starMapEditorTitle") }}</button>
    </div>

    <Transition name="toast-pop">
      <div v-if="toastText && !toastMapId" class="map-toast" :class="`is-${toastKind}`" role="status" aria-live="polite">
        {{ toastText }}
      </div>
    </Transition>
  </section>
</template>

<style scoped>
.manager { padding: 18px 22px 22px; }
.subpage-header { justify-content: flex-start; margin-bottom: 8px; }
.subview-title { display:grid; gap:3px; margin:0 0 10px; }
.subview-title strong { color:#eafffd; font-size:15px; }
.subview-title span { color:rgba(255,255,255,.42); font-size:10px; }
.map-search { position: relative; display: flex; align-items: center; gap: 8px; width: 100%; margin: 0 0 12px; padding: 0 12px; min-height: 42px; border: 1px solid rgba(82,246,232,.24); border-radius: 12px; background: rgba(4,25,41,.78); }
.search-icon { flex: 0 0 auto; color: #77f8ef; font-size: 20px; line-height: 1; transform: rotate(-12deg); }
.map-search input { min-width: 0; flex: 1; height: 40px; border: 0; outline: 0; color: #effffd; background: transparent; font: inherit; font-size: 13px; }
.map-search input::placeholder { color: rgba(255,255,255,.38); }
.map-search input::-webkit-search-cancel-button { display: none; }
.search-clear { flex: 0 0 28px; width: 28px; height: 28px; display: grid; place-items: center; border: 0; border-radius: 50%; padding: 0; color: #cafffb; background: rgba(82,246,232,.10); font-size: 19px; line-height: 1; }
.map-list { display: grid; gap: 10px; }
.safety-summary{padding:8px 12px;border-radius:10px;color:#8fffd2;background:rgba(68,221,160,.08);font-size:12px}.safety-summary.banned{color:#ff9aad;background:rgba(255,88,117,.1)}
.appeal-open{width:max-content;border:1px solid rgba(82,246,232,.3);border-radius:8px;padding:5px 9px;color:#dffffc;background:#09283a}.appeal-box{display:flex;gap:6px}.appeal-box textarea{min-height:58px;flex:1;color:#fff;background:#03131f;border:1px solid rgba(82,246,232,.2);border-radius:8px;padding:7px}.appeal-box button{border:1px solid #52f6e8;border-radius:8px;color:#fff;background:#0b6170}
.search-empty { min-height: 150px; display: grid; place-items: center; align-content: center; gap: 7px; text-align: center; color: rgba(255,255,255,.52); }
.search-empty strong { color: #effffd; font-size: 15px; }
.search-empty span { font-size: 11px; }
.search-empty button { margin-top: 4px; border: 1px solid rgba(82,246,232,.28); border-radius: 999px; padding: 7px 13px; color: #cffffb; background: rgba(5,31,47,.78); font-size: 11px; }
.map-card { position:relative; min-width:0; display:grid; grid-template-columns:130px minmax(0,1fr) auto; gap:14px; align-items:center; padding:14px; border:1px solid rgba(82,246,232,.18); border-radius:17px; background:rgba(4,25,41,.74); }

.map-card-toast {
  position:absolute;
  z-index:40;
  left:50%;
  top:-10px;
  width:min(360px, calc(100% - 28px));
  transform:translate(-50%,-100%);
  padding:10px 14px;
  border:1px solid rgba(113,255,243,.5);
  border-radius:12px;
  color:#eafffd;
  background:rgba(4,28,43,.97);
  box-shadow:0 10px 28px rgba(0,0,0,.42);
  font-size:12px;
  font-weight:800;
  line-height:1.4;
  text-align:center;
  pointer-events:none;
  backdrop-filter:blur(10px);
  -webkit-backdrop-filter:blur(10px);
}
.map-card-toast::after {
  content:"";
  position:absolute;
  left:50%;
  bottom:-6px;
  width:10px;
  height:10px;
  transform:translateX(-50%) rotate(45deg);
  border-right:1px solid rgba(113,255,243,.5);
  border-bottom:1px solid rgba(113,255,243,.5);
  background:inherit;
}
.map-card-toast.is-error { border-color:rgba(255,117,137,.68); color:#ffd6dc; background:rgba(54,18,30,.97); }
.map-card-toast.is-error::after { border-color:rgba(255,117,137,.68); }
.map-card-toast.is-success { border-color:rgba(113,255,243,.58); }
.card-toast-pop-enter-active,.card-toast-pop-leave-active { transition:opacity .14s ease, transform .14s ease; }
.card-toast-pop-enter-from,.card-toast-pop-leave-to { opacity:0; transform:translate(-50%,-92%) scale(.96); }

.map-preview { position: relative; width: 130px; aspect-ratio: 1.45; overflow: hidden; border: 0; border-radius: 10px; padding: 0; background: #010812; }
.map-preview img { width: 100%; height: 100%; object-fit: cover; }
.map-preview span { position: absolute; left: 6px; top: 6px; padding: 3px 6px; border-radius: 999px; color: #041018; background: #91fff6; font-size: 8px; font-weight: 900; }
.map-copy { display:grid; gap:6px; min-width:0; overflow:hidden; }
.map-copy strong { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:15px; }
.map-card-description { margin:0; color:rgba(235,255,252,.58); font-size:11px; line-height:1.45; display:-webkit-box; -webkit-box-orient:vertical; -webkit-line-clamp:2; overflow:hidden; overflow-wrap:anywhere; }
.map-copy > span { color:rgba(255,255,255,.5); font-size:10px; }
.map-copy > .moderation-status { width:max-content; max-width:100%; padding:3px 8px; border-radius:999px; color:#ffd56a; background:rgba(255,190,49,.1); }
.map-copy > .moderation-status.is-approved { color:#8fffd2; background:rgba(58,221,157,.1); }
.map-copy > .moderation-status.is-rejected,
.map-copy > .moderation-status.is-hidden { color:#ff9aad; background:rgba(255,88,117,.1); }
.map-copy label { display: flex; align-items: center; gap: 8px; }
.map-copy small { color: rgb(233, 229, 229); }
.map-copy select { min-height: 30px; border: 1px solid rgba(82,246,232,.24); border-radius: 8px; color: #fff; background: #061827; padding: 0 7px; }
.map-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.map-actions button { min-height:42px; border:1px solid rgba(82,246,232,.23); border-radius:10px; padding:10px 12px; color:#eafffd; background:rgba(7,36,52,.84); font-size:12px; font-weight:800; }
.map-actions .danger { border-color: rgba(255,105,120,.34); color: #ffb3bc; }
.empty-state { min-height: 310px; display: grid; place-items: center; align-content: center; gap: 9px; text-align: center; color: rgba(255,255,255,.58); }
.empty-state strong { color: #fff; font-size: 18px; }
.empty-state span { max-width: 420px; font-size: 12px; line-height: 1.55; }
.map-toast {
  position:fixed;
  z-index:2200;
  top:50%;
  left:50%;
  width:min(360px, calc(100vw - 48px));
  transform:translate(-50%,-50%);
  padding:14px 18px;
  border:1px solid rgba(113,255,243,.42);
  border-radius:15px;
  color:#eafffd;
  background:rgba(4,28,43,.97);
  box-shadow:0 18px 48px rgba(0,0,0,.48);
  font-size:14px;
  font-weight:800;
  line-height:1.48;
  text-align:center;
  pointer-events:none;
  backdrop-filter:blur(12px);
  -webkit-backdrop-filter:blur(12px);
}
.map-toast.is-success { border-color:rgba(113,255,243,.54); }
.map-toast.is-error { border-color:rgba(255,117,137,.62); color:#ffd6dc; background:rgba(54,18,30,.96); }
.toast-pop-enter-active,.toast-pop-leave-active { transition:opacity .18s ease, transform .18s ease; }
.toast-pop-enter-from,.toast-pop-leave-to { opacity:0; transform:translate(-50%,-50%) scale(.94); }
@media (max-width:650px) {
  .manager {
    width: 100%;
    min-height: calc(100dvh - 28px);
    padding: 10px 10px 12px;
    border-radius: 18px;
  }
  .subpage-header {
    align-items: center;
    margin-bottom: 5px;
  }
  .subpage-header .back-button {
    padding: 3px 0;
    white-space: nowrap;
  }
  .subview-title { margin-bottom: 7px; }
  .map-search { min-height: 46px; margin-bottom: 10px; padding: 0 12px; }
  .map-search input { height: 44px; font-size: 15px; }
  .search-icon { font-size: 22px; }
  .search-clear { width: 30px; height: 30px; flex-basis: 30px; }
  .map-card { grid-template-columns:104px minmax(0,1fr); align-items:start; gap:12px; padding:14px; border-radius:18px; }
  .map-preview { width:104px; }
  .map-card-toast { top:-8px; width:min(330px, calc(100vw - 48px)); padding:10px 12px; font-size:13px; }
  .map-copy strong { font-size:17px; }
  .map-card-description { font-size:13px; line-height:1.45; }
  .map-copy > span { font-size:12px; }
  .map-copy label { align-items:flex-start; }
  .map-copy small { font-size:15px;}
  .map-copy select { min-height:38px; font-size:14px; }
  .map-actions { grid-column:1/-1; grid-template-columns:repeat(4,1fr); gap:8px; }
  .map-actions button { min-height:48px; padding:11px 7px; font-size:14px; }
  .map-toast {
    top:48%;
    width:min(330px, calc(100vw - 44px));
    padding:14px 16px;
    font-size:14px;
  }
}
@media (max-width:390px) {
  .map-actions { grid-template-columns: 1fr 1fr; }
}
</style>
